import { useCallback, useEffect, useRef, useState } from 'react'

import { useLatestCallback } from './useLatestCallback'

interface SpeechRecognitionEvent extends Event {
  resultIndex: number
  results: SpeechRecognitionResultList
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

interface SpeechRecognitionType extends EventTarget {
  abort: () => void
  continuous: boolean
  interimResults: boolean
  lang: string
  onend: (() => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  start: () => void
  stop: () => void
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionType
    webkitSpeechRecognition?: new () => SpeechRecognitionType
  }
}

export enum SpeechRecognitionErrorType {
  MICROPHONE_DENIED = 'microphone-denied',
  NETWORK_ERROR = 'network-error',
  NO_SPEECH = 'no-speech',
  ABORTED = 'aborted',
  AUDIO_CAPTURE = 'audio-capture',
  START_FAILED = 'start-failed',
  UNKNOWN = 'unknown',
}

export interface SpeechRecognitionError {
  code: string
  message?: string
  type: SpeechRecognitionErrorType
}

export interface SpeechRecognitionConfig {
  autoStop: boolean
  onError?: (error: SpeechRecognitionError) => void
  onTranscriptComplete?: (transcript: string) => void
}

export interface UseSpeechRecognitionReturn {
  audioStream: MediaStream | null
  clearError: () => void
  error: string | null
  errorDetails: SpeechRecognitionError | null
  isListening: boolean
  isSupported: boolean
  startListening: () => void
  stopListening: () => void
  transcript: string
}

const NotAllowedStatus = 'not-allowed'

const mapErrorToType = (errorCode: string): SpeechRecognitionErrorType => {
  switch (errorCode) {
    case NotAllowedStatus:
      return SpeechRecognitionErrorType.MICROPHONE_DENIED
    case 'network':
      return SpeechRecognitionErrorType.NETWORK_ERROR
    case 'no-speech':
      return SpeechRecognitionErrorType.NO_SPEECH
    case 'aborted':
      return SpeechRecognitionErrorType.ABORTED
    case 'audio-capture':
      return SpeechRecognitionErrorType.AUDIO_CAPTURE
    default:
      return SpeechRecognitionErrorType.UNKNOWN
  }
}

/**
 * Custom hook for using the Web Speech API (SpeechRecognition)
 */
export const useSpeechRecognition = (
  config: SpeechRecognitionConfig,
): UseSpeechRecognitionReturn => {
  const { autoStop, onTranscriptComplete, onError } = config

  // Check browser support
  const SpeechRecognitionAPI =
    window.SpeechRecognition || window.webkitSpeechRecognition

  const isSupported = Boolean(SpeechRecognitionAPI)

  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] =
    useState<SpeechRecognitionError | null>(null)
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null)

  const recognitionRef = useRef<SpeechRecognitionType | null>(null)
  const silenceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const finalizedTranscriptRef = useRef('')
  const isListeningRef = useRef(false)
  const onTranscriptCompleteRef = useLatestCallback(onTranscriptComplete)
  const onErrorRef = useLatestCallback(onError)

  // Check microphone permissions on mount
  useEffect(() => {
    if (!isSupported) return () => {}

    let permissionStatus: PermissionStatus | null = null

    const checkMicrophonePermission = async () => {
      try {
        if (
          navigator.permissions &&
          typeof navigator.permissions.query === 'function'
        ) {
          permissionStatus = await navigator.permissions.query({
            name: 'microphone' as PermissionName,
          })

          if (permissionStatus.state === 'denied') {
            const errorDetailsValue: SpeechRecognitionError = {
              type: SpeechRecognitionErrorType.MICROPHONE_DENIED,
              code: NotAllowedStatus,
            }

            setError(NotAllowedStatus)
            setErrorDetails(errorDetailsValue)

            if (onErrorRef.current) {
              onErrorRef.current(errorDetailsValue)
            }
          }

          permissionStatus.onchange = () => {
            if (permissionStatus?.state === 'denied') {
              const errorDetailsValue: SpeechRecognitionError = {
                type: SpeechRecognitionErrorType.MICROPHONE_DENIED,
                code: NotAllowedStatus,
              }

              setError(NotAllowedStatus)
              setErrorDetails(errorDetailsValue)

              if (onErrorRef.current) {
                onErrorRef.current(errorDetailsValue)
              }
            } else if (permissionStatus?.state === 'granted') {
              setError(null)
              setErrorDetails(null)
            }
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.debug('Could not check microphone permissions:', err)
      }
    }

    checkMicrophonePermission()

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null
      }
    }
  }, [isSupported, onErrorRef])

  // Initialize SpeechRecognition instance
  useEffect(() => {
    if (!isSupported || !SpeechRecognitionAPI) return undefined

    const recognition = new SpeechRecognitionAPI()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcriptPart = result[0].transcript

        if (result.isFinal) {
          finalTranscript += `${transcriptPart} `
        } else {
          interimTranscript += transcriptPart
        }
      }

      if (finalTranscript) {
        finalizedTranscriptRef.current =
          `${finalizedTranscriptRef.current} ${finalTranscript}`.trim()

        // Reset silence timer for auto-stop
        if (autoStop) {
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current)
          }
          // Stop after 2 seconds of silence
          silenceTimerRef.current = setTimeout(() => {
            recognition.stop()
          }, 2000)
        }
      }

      const displayTranscript = interimTranscript
        ? `${finalizedTranscriptRef.current} ${interimTranscript}`.trim()
        : finalizedTranscriptRef.current

      setTranscript(displayTranscript)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      // eslint-disable-next-line no-console
      console.error('Speech recognition error:', event.error)

      const errorType = mapErrorToType(event.error)

      const errorDetailsValue: SpeechRecognitionError = {
        type: errorType,
        code: event.error,
        message: event.message,
      }

      setError(event.error)
      setErrorDetails(errorDetailsValue)

      // Call user-provided error handler if available
      if (onErrorRef.current) {
        onErrorRef.current(errorDetailsValue)
      }

      setIsListening(false)
      isListeningRef.current = false
    }

    recognition.onend = () => {
      setIsListening(false)
      isListeningRef.current = false

      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }

      // Trigger callback with final transcript
      const finalText = finalizedTranscriptRef.current.trim()
      if (finalText && onTranscriptCompleteRef.current) {
        onTranscriptCompleteRef.current(finalText)
      }

      setAudioStream((prevStream) => {
        if (prevStream) {
          prevStream.getTracks().forEach((track) => track.stop())
        }
        return null
      })
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
      }
    }
  }, [isSupported, autoStop, onTranscriptCompleteRef, onErrorRef])

  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return

    setError(null)
    setErrorDetails(null)
    setTranscript('')
    finalizedTranscriptRef.current = ''
    setIsListening(true)
    isListeningRef.current = true

    try {
      recognitionRef.current.start()

      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          if (!isListeningRef.current) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }
          setAudioStream(stream)
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(
            'Failed to get microphone access for visualization:',
            err,
          )
        })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to start speech recognition:', err)
      setIsListening(false)
      isListeningRef.current = false

      const errorDetailsValue: SpeechRecognitionError = {
        type: SpeechRecognitionErrorType.START_FAILED,
        code: 'start-failed',
        message: err instanceof Error ? err.message : undefined,
      }

      setError('start-failed')
      setErrorDetails(errorDetailsValue)

      if (onErrorRef.current) {
        onErrorRef.current(errorDetailsValue)
      }
    }
  }, [isListening, onErrorRef])

  const stopListening = useCallback(() => {
    if (!recognitionRef.current || !isListening) return

    isListeningRef.current = false
    recognitionRef.current.stop()

    setAudioStream((prevStream) => {
      if (prevStream) {
        prevStream.getTracks().forEach((track) => track.stop())
      }
      return null
    })
  }, [isListening])

  const clearError = useCallback(() => {
    setError(null)
    setErrorDetails(null)
  }, [])

  return {
    audioStream,
    clearError,
    error,
    errorDetails,
    isListening,
    isSupported,
    startListening,
    stopListening,
    transcript,
  }
}
